import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../data';
import SocialRow from './SocialRow';
import './Navbar.css';

/*
 * Shared navbar (SPEC §5). `variant`:
 *   'main' → 5-item menu (ABOUT · STRUCTURAL · INFRASTRUCTURE · RESEARCH · CONTACT)
 *   'page' → 4-item menu (HOME · STRUCTURAL · INFRASTRUCTURE · RESEARCH)
 * The logo links to the homepage. Mobile uses a morphing hamburger + animated
 * full-screen panel with staggered link reveals.
 */
export default function Navbar({ variant = 'main' }) {
  const nav = useNavigation();
  const items = (variant === 'page' ? nav.pageMenu : nav.mainMenu)
    .slice()
    .sort((a, b) => a.order - b.order);

  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const isActive = (href) => !href.includes('#') && href === location.pathname;

  // Multi-word labels (e.g. "STRUCTURAL ENGINEERING") render on two lines: the
  // category on top, the trailing word as a smaller cyan sub-line.
  const renderLabel = (label) => {
    const words = (label || '').trim().split(/\s+/);
    if (words.length < 2) return label;
    const last = words.pop();
    return (
      <>
        <span className="navbar__link-l1">{words.join(' ')}</span>
        <span className="navbar__link-l2">{last}</span>
      </>
    );
  };

  const renderLink = (item, extraProps = {}) => {
    const stacked = (item.label || '').trim().split(/\s+/).length > 1;
    const cls = `navbar__link${stacked ? ' navbar__link--stacked' : ''}${isActive(item.href) ? ' is-active' : ''}`;
    return item.href.includes('#') ? (
      <a key={item.label} className={cls} href={item.href} {...extraProps}>
        {renderLabel(item.label)}
      </a>
    ) : (
      <Link key={item.label} className={cls} to={item.href} {...extraProps}>
        {renderLabel(item.label)}
      </Link>
    );
  };

  return (
    <>
      <header className="navbar">
        <div className="navbar__inner container">
        <Link to="/" className="navbar__logo" aria-label="Moab Planning & Civil Engineering — home">
          <img src="/assets/logo/logo.png" alt="Moab Planning & Civil Engineering" />
        </Link>

        <nav className="navbar__menu" aria-label="Primary">
          {items.map((it) => renderLink(it))}
        </nav>

        <div className="navbar__social">
          <SocialRow variant="plain" order={['facebook', 'linkedin']} size={18} />
        </div>

        <button
          className={`navbar__toggle${open ? ' is-open' : ''}`}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="hamburger" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
        </div>
      </header>

      {/* Mobile overlay — a SIBLING of the bar (not a child): the navbar's
          backdrop-filter creates a containing block that would otherwise trap
          this fixed element and collapse it. */}
      <div className={`navbar__mobile${open ? ' is-open' : ''}`} onClick={() => setOpen(false)}>
        <nav className="navbar__mobile-menu" aria-label="Mobile" onClick={(e) => e.stopPropagation()}>
          {items.map((it, i) => renderLink(it, { style: { '--i': i } }))}
          <div className="navbar__mobile-social" style={{ '--i': items.length }}>
            <SocialRow variant="plain" order={['facebook', 'linkedin']} size={22} />
          </div>
        </nav>
      </div>
    </>
  );
}
